/* CO-ORD theme behaviour.
 *
 * Everything the site did with React islands, done with the platform. Nothing
 * here is load-bearing for a purchase: the product form is a real POST to
 * Shopify's own /cart/add, so a customer can buy with this file blocked,
 * failed or still downloading. What script adds is remembered sizes, adding to
 * the bag without leaving the page, and the sticky bar.
 *
 * Ordered as the page uses it: chrome, then the bag, then the product, then
 * bundles.
 */
(function () {
  "use strict";

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var live = document.getElementById("live-region");

  function say(message) {
    if (live) live.textContent = message;
  }

  function money(cents) {
    var amount = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
    return "$" + amount;
  }

  /* ---------- Header ----------
     Only touch the DOM when the answer changes. This used to write on every
     scroll event — hundreds a second during a drag — and each write
     invalidates style for the whole document. */
  var header = document.querySelector("[data-header]");
  if (header) {
    var stuck = null;
    var onScroll = function () {
      var scrolled = window.scrollY > 8;
      if (scrolled === stuck) return;
      stuck = scrolled;
      if (scrolled) {
        header.setAttribute("data-stuck", "");
        document.documentElement.setAttribute("data-scrolled", "");
      } else {
        header.removeAttribute("data-stuck");
        document.documentElement.removeAttribute("data-scrolled");
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------- Images fade in ----------
     Only below-the-fold images are candidates, and the attribute that drives
     it is added here rather than in the markup: with JavaScript off nothing is
     ever transparent, so nothing can be stuck invisible. */
  if (!reduced) {
    document.querySelectorAll("img[data-fadeable]").forEach(function (img) {
      if (img.complete) return;
      img.setAttribute("data-fade", "");
      img.addEventListener(
        "load",
        function () {
          img.setAttribute("data-loaded", "");
        },
        { once: true },
      );
    });
  }

  /* ---------- The bag ---------- */
  var bagRoot = document.querySelector("[data-bag-root]");
  var lastFocus = null;

  /* The scroll lock goes on <html>, never on <body>.
   *
     The header is position: sticky and lives in the body. Setting
     overflow: hidden on the body makes it the scroll container, which takes
     the header's sticky containing block away — the header vanishes and the
     page appears to jump back to the top. Locking the root element instead
     leaves the sticky context alone, which is what the site it was ported
     from does. */
  function lockScroll(on) {
    document.documentElement.style.overflow = on ? "hidden" : "";
  }

  function bagPanel() {
    return bagRoot ? bagRoot.querySelector("[data-bag-panel]") : null;
  }

  function openBag() {
    if (!bagRoot) return;
    /* Only remember where focus was if the bag is not already open, or
       reopening it would make the drawer itself the thing to return to. */
    if (bagRoot.hidden) lastFocus = document.activeElement;
    bagRoot.hidden = false;
    lockScroll(true);
    var panel = bagPanel();
    if (panel) {
      var first = panel.querySelector(
        'button, a[href], input, select, [tabindex]:not([tabindex="-1"])',
      );
      (first || panel).focus();
    }
  }

  function closeBag() {
    if (!bagRoot) return;
    bagRoot.hidden = true;
    lockScroll(false);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  document.addEventListener("click", function (event) {
    if (event.target.closest("[data-bag-open]")) {
      event.preventDefault();
      openBag();
    } else if (event.target.closest("[data-bag-close]")) {
      closeBag();
    }
  });

  document.addEventListener("keydown", function (event) {
    if (!bagRoot || bagRoot.hidden) return;
    if (event.key === "Escape") {
      closeBag();
      return;
    }
    /* Focus stays inside the drawer while it is open. Without this, tabbing
       walks out into the page behind it, which for a screen reader user is a
       dialog that silently stopped being one. */
    if (event.key !== "Tab") return;
    var panel = bagRoot.querySelector("[data-bag-panel]");
    if (!panel) return;
    var items = panel.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), select, [tabindex]:not([tabindex="-1"])',
    );
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Re-render the drawer from Shopify own markup, in one request.
   *
     This used to make three round trips in sequence: a pointless section_id
     call that fetched nothing, then /cart.js, then the **entire current page**
     re-downloaded and run through DOMParser just to lift the drawer out of it.
     On a product page that is 38KB of HTML parsed twice to update a panel, and
     it is why adding to the bag felt slow.

     The drawer is a section now, so Shopify Section Rendering API returns just
     that section markup — a few KB — and the count comes out of the same
     response rather than a second call. */
  /* Shopify can render a section and return it inside the cart response, so a
     change and its re-render are one round trip rather than two. Pass the HTML
     straight to this instead of fetching it again. */
  function applyBag(html, openAfter) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    var fresh = doc.querySelector("[data-bag-root]");
    if (!fresh || !bagRoot) return;

    var shouldOpen = openAfter || !bagRoot.hidden;

    /* Swap the panel's contents, not the panel.
     *
       .bag-panel carries `animation: slideIn`, and a CSS animation runs when
       its element is created. Replacing bagRoot.innerHTML destroys the panel
       and builds a new one, so the slide replayed on every refresh — the
       drawer appeared to shut and open again each time something was added or
       a quantity changed. Writing into the existing panel keeps the element
       alive, so the animation stays where it belongs: on the actual open.

       The full replacement is still the path when the drawer is closed, where
       there is no animation to disturb and the next open should play it. */
    var panel = bagRoot.querySelector("[data-bag-panel]");
    var freshPanel = fresh.querySelector("[data-bag-panel]");
    if (panel && freshPanel && !bagRoot.hidden) {
      panel.innerHTML = freshPanel.innerHTML;
    } else {
      bagRoot.innerHTML = fresh.innerHTML;
    }

    /* The contents are real now, so the placeholder comes off. */
    bagRoot.removeAttribute("data-pending");
    bagRoot.hidden = !shouldOpen;
    if (shouldOpen) lockScroll(true);

    var freshCount = fresh.querySelector("[data-bag-title-count]");
    var total = freshCount ? parseInt(freshCount.getAttribute("data-count"), 10) : 0;
    var button = document.querySelector("[data-bag-open]");
    var count = document.querySelector("[data-bag-count]");

    if (!total) {
      if (count) count.remove();
    } else if (button) {
      if (!count) {
        count = document.createElement("span");
        count.className = "hdr-bagCount";
        count.setAttribute("data-bag-count", "");
        count.setAttribute("aria-hidden", "true");
        button.appendChild(count);
      }
      count.textContent = total;
      count.setAttribute("data-bumped", "");
      window.setTimeout(function () {
        count.removeAttribute("data-bumped");
      }, 600);
    }
    if (button) {
      button.setAttribute(
        "aria-label",
        "Bag, " + total + (total === 1 ? " outfit" : " outfits"),
      );
    }
  }

  function refreshBag(openAfter) {
    var url =
      window.location.pathname +
      (window.location.search ? window.location.search + "&" : "?") +
      "section_id=bag-drawer";

    return fetch(url, { headers: { "X-Requested-With": "fetch" } })
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var fresh = doc.querySelector("[data-bag-root]");
        if (!fresh || !bagRoot) return;

        var shouldOpen = openAfter || !bagRoot.hidden;
        bagRoot.innerHTML = fresh.innerHTML;
        bagRoot.hidden = !shouldOpen;
        if (shouldOpen) {
          lockScroll(true);
          var panel = bagPanel();
          if (panel) {
            var first = panel.querySelector(
              'button, a[href], input, select, [tabindex]:not([tabindex="-1"])',
            );
            (first || panel).focus();
          }
        }

        /* The count comes from the markup that was just rendered, so there is
           no second request and no chance of the two disagreeing. */
        var freshCount = fresh.querySelector("[data-bag-title-count]");
        var total = freshCount ? parseInt(freshCount.getAttribute("data-count"), 10) : 0;
        var button = document.querySelector("[data-bag-open]");
        var count = document.querySelector("[data-bag-count]");

        if (!total) {
          if (count) count.remove();
        } else if (button) {
          if (!count) {
            count = document.createElement("span");
            count.className = "hdr-bagCount";
            count.setAttribute("data-bag-count", "");
            count.setAttribute("aria-hidden", "true");
            button.appendChild(count);
          }
          count.textContent = total;
          count.setAttribute("data-bumped", "");
          window.setTimeout(function () {
            count.removeAttribute("data-bumped");
          }, 600);
        }
        if (button) {
          button.setAttribute(
            "aria-label",
            "Bag, " + total + (total === 1 ? " outfit" : " outfits"),
          );
        }
      });
  }

  document.addEventListener("click", function (event) {
    var up = event.target.closest("[data-qty-up]");
    var down = event.target.closest("[data-qty-down]");
    var remove = event.target.closest("[data-remove]");
    var control = up || down || remove;
    if (!control) return;

    event.preventDefault();
    var key = control.getAttribute("data-key");
    var line = control.closest("[data-line]");
    var current = line
      ? parseInt(line.querySelector("[data-qty-value]").textContent, 10)
      : 1;
    var quantity = remove ? 0 : up ? current + 1 : Math.max(0, current - 1);

    /* Show the new number now. The request still has to go and come back, and
       the drawer is replaced wholesale when it does — but a stepper that does
       not move until the network answers is a stepper people press again. The
       controls are disabled meanwhile so a second press cannot race the first
       and send a quantity based on a number already out of date. */
    var value = line ? line.querySelector("[data-qty-value]") : null;
    if (value && !remove) value.textContent = quantity;
    if (bagRoot) {
      bagRoot.querySelectorAll("[data-qty-up], [data-qty-down], [data-remove]").forEach(
        function (el) {
          el.disabled = true;
        },
      );
    }

    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
        sections: "bag-drawer",
      }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (data.sections && data.sections["bag-drawer"]) {
          applyBag(data.sections["bag-drawer"], true);
          return;
        }
        refreshBag(true);
      });
  });

  /* ---------- Sizes remembered for the session ----------
     Somebody buying a second outfit has already told us their sizes once. */
  var MEMORY = "coord.sizes";

  function remembered() {
    try {
      return JSON.parse(window.sessionStorage.getItem(MEMORY) || "{}");
    } catch (e) {
      return {};
    }
  }

  function remember(option, value) {
    try {
      var all = remembered();
      all[option] = value;
      window.sessionStorage.setItem(MEMORY, JSON.stringify(all));
    } catch (e) {
      /* Private mode. Nothing here is load-bearing. */
    }
  }

  /* ---------- One buy form ---------- */
  function wireBuyForm(form) {
    var mapNode = document.querySelector("[data-variant-map]");
    if (!mapNode) return;
    var variants = JSON.parse(mapNode.textContent);
    var idField = form.querySelector("[data-variant-id]");
    var asking = form.querySelector("[data-asking]");
    var actions = form.querySelector("[data-actions]");
    var buyButtons = document.querySelectorAll("[data-buy-now]");
    var addButton = form.querySelector("[data-add-to-bag]");

    /* By data-option, not by name. The name now carries a per-outfit prefix so
       that four outfits in one bundle form are four separate radio groups; see
       the note in snippets/size-picker.liquid. */
    function chosen(index) {
      var input = form.querySelector(
        'input[data-option="' + index + '"]:checked',
      );
      return input ? input.value : null;
    }

    function resolve() {
      var a = chosen(1);
      var b = chosen(2);
      if (!a || !b) return null;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].o1 === a && variants[i].o2 === b) return variants[i];
      }
      return null;
    }

    /* Both size groups answered. Read by the sticky bar below, which is
       defined after this function — hence the forward-declared no-op, so the
       first sync() before the bar is wired does not throw. */
    var sizesChosen = false;
    var refreshBar = function () {};

    function sync() {
      var variant = resolve();
      var complete = Boolean(variant);
      sizesChosen = complete;
      refreshBar();
      if (idField) idField.value = variant ? variant.id : "";

      buyButtons.forEach(function (button) {
        button.setAttribute("aria-disabled", complete ? "false" : "true");
        if (button.closest("[data-buy-bar]")) {
          button.textContent = complete ? "Buy now" : "Select sizes";
        } else {
          button.textContent = complete ? "Buy now" : "Select your sizes";
        }
      });
      if (addButton) {
        addButton.setAttribute("aria-disabled", complete ? "false" : "true");
      }

      /* The moment the outstanding size is chosen the question stops being
         outstanding. If one is still missing the prompt follows it across. */
      if (complete) {
        clearAsking();
      } else if (asking && !asking.hidden) {
        ask(chosen(1) ? 2 : 1);
      }
    }

    function clearAsking() {
      if (asking) {
        asking.hidden = true;
        asking.textContent = "";
      }
      form.querySelectorAll("[data-size-group]").forEach(function (group) {
        group.removeAttribute("data-missing");
      });
    }

    /* Say what is missing, rather than only moving focus to it. Focus alone is
       invisible when the picker is already on screen, which is the normal case
       on a phone, and the button read as dead. */
    function ask(index) {
      var label = index === 1 ? "tee" : "bottom";
      if (asking) {
        asking.textContent = "Choose a " + label + " size to continue.";
        asking.hidden = false;
      }
      form.querySelectorAll("[data-size-group]").forEach(function (group) {
        if (group.getAttribute("data-option") === String(index)) {
          group.setAttribute("data-missing", "");
        } else {
          group.removeAttribute("data-missing");
        }
      });

      var group = form.querySelector(
        '[data-size-group][data-option="' + index + '"]',
      );
      if (!group) return;
      /* The whole fieldset, centred. focus() alone scrolls the bare minimum,
         which parks the picker under the sticky header. */
      group.scrollIntoView({
        block: "center",
        behavior: reduced ? "auto" : "smooth",
      });
      var first = group.querySelector("input:not([disabled])");
      if (first) first.focus({ preventScroll: true });
    }

    form.addEventListener("change", function (event) {
      var input = event.target.closest("[data-size-input]");
      if (!input) return;
      remember(input.name, input.value);
      sync();
    });

    /* Restore what they chose last time, but only for sizes this product
       actually offers. */
    var saved = remembered();
    Object.keys(saved).forEach(function (name) {
      var input = form.querySelector(
        'input[name="' + name + '"][value="' + saved[name] + '"]:not([disabled])',
      );
      if (input) input.checked = true;
    });
    sync();

    /* Guard both buttons. Without both sizes the form must not submit — the
       hidden id is empty and Shopify would answer with an error page. */
    document.addEventListener("click", function (event) {
      var button = event.target.closest("[data-buy-now], [data-add-to-bag]");
      if (!button) return;
      if (button.getAttribute("aria-disabled") === "true") {
        event.preventDefault();
        ask(chosen(1) ? 2 : 1);
        return;
      }
      /* Add to bag is always intercepted: it must never navigate away or lose
         scroll position. Buy now is left alone — the native POST is the fast
         path and goes straight to checkout. */
      if (button.hasAttribute("data-add-to-bag")) {
        event.preventDefault();
        var variant = resolve();
        if (!variant) return;
        button.setAttribute("data-busy", "");
        /* Open on the tap, not when the network comes back.
           The request still has to happen, but nobody should be looking at an
           unchanged page wondering whether the button worked — that is the
           whole of what "laggy" meant here. The panel opens showing what is
           already in the bag and fills in when the response lands.

           data-pending marks that gap. Without it the first add to an empty
           bag opened on "Your bag is empty" for as long as the request took,
           which is the worst possible thing to show somebody who has just
           pressed Add to bag. The CSS swaps that message for a placeholder
           line, and applyBag clears the flag when the real contents arrive. */
        openBag();
        bagRoot.setAttribute("data-pending", "");
        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1 }],
            /* The drawer comes back rendered in this same response. */
            sections: "bag-drawer",
          }),
        })
          .then(function (res) {
            return res.json();
          })
          .then(function (data) {
            if (data.sections && data.sections["bag-drawer"]) {
              applyBag(data.sections["bag-drawer"], true);
              return;
            }
            return refreshBag(true);
          })
          .then(function () {
            button.removeAttribute("data-busy");
            button.setAttribute("data-done", "");
            say("Added to your bag");
            window.setTimeout(function () {
              button.removeAttribute("data-done");
            }, 2200);
          });
      }
    });

    /* ---------- The sticky bar ----------
       The real buttons must be off screen, and then either the customer has
       scrolled or they have chosen both sizes. Plain intersection, no margins
       — reserving the bar's own height drew it while 83px of the real Buy now
       was still showing above it, which is two buy controls stacked.

       The "or they have chosen both sizes" half is what keeps a buy control
       reachable now that the photograph is the full width of the screen and
       the real buttons start below the fold. On the old scroll-only rule a
       customer could pick a tee size, pick a bottom size, and be looking at a
       page with nothing to press: tap-count.mjs got exactly that far and then
       could not find a Buy now anywhere. Nothing is chosen on load, so the bar
       still stays out of the way of somebody who has only just arrived. */
    var bar = document.querySelector("[data-buy-bar]");
    if (bar && actions) {
      var hidden = false;
      var scrolledOnce = window.scrollY > 24;

      var update = function () {
        var show = (scrolledOnce || sizesChosen) && hidden;
        if (show) {
          bar.setAttribute("data-visible", "");
          bar.removeAttribute("inert");
        } else {
          bar.removeAttribute("data-visible");
          bar.setAttribute("inert", "");
        }
      };

      new IntersectionObserver(
        function (entries) {
          hidden = !entries[0].isIntersecting;
          update();
        },
        { threshold: 0 },
      ).observe(actions);

      if (!scrolledOnce) {
        var mark = function () {
          if (window.scrollY > 24) {
            scrolledOnce = true;
            update();
            window.removeEventListener("scroll", mark);
          }
        };
        window.addEventListener("scroll", mark, { passive: true });
      }
      /* From here the size handler can redraw the bar. */
      refreshBar = update;
      update();
    }
  }

  var buyForm = document.querySelector("[data-buy-form]");
  if (buyForm) wireBuyForm(buyForm);

  /* ---------- The clip ----------
     Tabbing to a <video controls> puts focus inside Chrome's own shadow DOM,
     so the video never matches :focus and no rule on it can paint a ring. The
     figure carries it with :focus-within. The disc is only an affordance over
     the poster; the native controls stay. */
  document.querySelectorAll("[data-video]").forEach(function (frame) {
    var video = frame.querySelector("video");
    var play = frame.querySelector("[data-video-play]");
    if (!video || !play) return;

    /* A clip set to autoplay needs no disc — it is already playing, and its
       controls stay so it can be paused. Only a clip that waits for a tap gets
       the disc instead of a control bar across an unplayed poster.

       Reduced motion is the exception: autoplay comes off, and it becomes a
       poster with a disc that plays when asked. Someone who has told their
       system they do not want movement should not be handed a looping video
       because a theme setting said so. */
    var autoplays = frame.hasAttribute("data-autoplay") && !reduced;

    if (reduced) {
      video.removeAttribute("autoplay");
      video.removeAttribute("loop");
      video.pause();
    }

    if (autoplays) {
      play.hidden = true;
      frame.setAttribute("data-started", "");

      /* Play when it reaches the screen, not when the page loads.
       *
         These clips are 4-9MB each. With `autoplay` in the markup the browser
         begins fetching immediately on a page where the clip sits below the
         details, the reviews and half the gallery — so it competed with the
         photographs for bandwidth on exactly the connections that could least
         afford it. That is what made the product page crawl and what left
         images unpainted: the bytes were going to a video nobody had scrolled
         to yet.

         The markup now carries `preload="none"` and no `autoplay` attribute,
         so nothing is fetched until this observer calls play(). It still plays
         by itself, still loops, still muted — it just waits until it is worth
         paying for. Pausing on the way out stops a clip decoding off-screen
         for the rest of the session.

         No IntersectionObserver (very old browser) means the clip keeps its
         poster and its controls, which is a working video, not a broken one. */
      if (!("IntersectionObserver" in window)) {
        video.setAttribute("controls", "");
        return;
      }

      var seen = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              var playing = video.play();
              if (playing && playing.catch) playing.catch(function () {});
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { rootMargin: "200px 0px", threshold: 0.15 },
      );
      seen.observe(frame);
      return;
    }

    video.removeAttribute("controls");
    play.hidden = false;

    var start = function () {
      video.setAttribute("controls", "");
      frame.setAttribute("data-started", "");
      play.hidden = true;
      /* Rejected autoplay is a promise nobody is waiting on; swallow it
         rather than let it surface as an unhandled rejection. */
      var playing = video.play();
      if (playing && playing.catch) playing.catch(function () {});
    };

    play.addEventListener("click", start);
    video.addEventListener("play", function () {
      video.setAttribute("controls", "");
      frame.setAttribute("data-started", "");
      play.hidden = true;
    });
  });

  /* ---------- The bundle builder ----------
     Gets the right number of correctly sized outfits into the cart in one
     request. Shopify's automatic discount does the arithmetic at checkout;
     nothing here applies a code or promises a saving the store will not give.
  */
  var bundle = document.querySelector("[data-bundle]");
  if (bundle) {
    var size = parseInt(bundle.getAttribute("data-size"), 10);
    var form = bundle.querySelector("[data-bundle-form]");
    var addBundle = bundle.querySelector("[data-bundle-add]");
    var prompt = bundle.querySelector("[data-bundle-prompt]");
    var listOut = bundle.querySelector("[data-bundle-list]");
    var offOut = bundle.querySelector("[data-bundle-off]");
    var totalOut = bundle.querySelector("[data-bundle-total]");
    var percentNode = bundle.querySelector("[data-bundle-off]");
    var percent = 0;
    var saveRow = bundle.querySelector(".bb-totalRow span");
    if (percentNode) {
      var match = percentNode.parentNode.textContent.match(/(\d+)%/);
      if (match) percent = parseInt(match[1], 10);
    }

    var cards = Array.prototype.slice.call(
      bundle.querySelectorAll("[data-outfit]"),
    );

    function cardVariant(card) {
      var map = JSON.parse(card.querySelector("[data-variant-map]").textContent);
      var a = card.querySelector('input[data-option="1"]:checked');
      var b = card.querySelector('input[data-option="2"]:checked');
      if (!a || !b) return null;
      for (var i = 0; i < map.length; i++) {
        if (map[i].o1 === a.value && map[i].o2 === b.value) return map[i];
      }
      return null;
    }

    function chosenCards() {
      return cards.filter(function (card) {
        return card.hasAttribute("data-chosen");
      });
    }

    function refresh() {
      var picked = chosenCards();
      var sized = picked.filter(function (card) {
        return cardVariant(card);
      });
      var list = picked.reduce(function (sum, card) {
        return sum + parseInt(card.getAttribute("data-price"), 10);
      }, 0);
      var off = Math.round((list * percent) / 100);

      if (listOut) listOut.textContent = money(list);
      if (offOut) offOut.textContent = "-" + money(off);
      if (totalOut) totalOut.textContent = money(list - off);

      var ready = picked.length === size && sized.length === size;
      if (addBundle) {
        addBundle.setAttribute("aria-disabled", ready ? "false" : "true");
      }

      if (prompt) {
        if (picked.length < size) {
          prompt.textContent =
            "Pick " + (size - picked.length) + " more.";
          prompt.hidden = false;
        } else if (sized.length < size) {
          prompt.textContent = "Choose both sizes for every outfit.";
          prompt.hidden = false;
        } else {
          prompt.hidden = true;
        }
      }
    }

    bundle.addEventListener("change", function (event) {
      var include = event.target.closest("[data-include]");
      if (include) {
        var card = include.closest("[data-outfit]");
        if (include.checked) {
          if (chosenCards().length >= size) {
            include.checked = false;
            return;
          }
          card.setAttribute("data-chosen", "");
        } else {
          card.removeAttribute("data-chosen");
        }
      }
      var sizeInput = event.target.closest("[data-size-input]");
      if (sizeInput) remember(sizeInput.name, sizeInput.value);
      refresh();
    });

    if (form) {
      form.addEventListener("submit", function (event) {
        event.preventDefault();
        if (addBundle.getAttribute("aria-disabled") === "true") {
          refresh();
          return;
        }
        var items = chosenCards()
          .map(function (card) {
            var variant = cardVariant(card);
            return variant ? { id: variant.id, quantity: 1 } : null;
          })
          .filter(Boolean);

        addBundle.setAttribute("data-busy", "");
        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: items }),
        })
          .then(function () {
            return refreshBag(true);
          })
          .then(function () {
            addBundle.removeAttribute("data-busy");
            say("Added to your bag");
          });
      });
    }

    /* Restore remembered sizes here too. */
    var savedBundle = remembered();
    cards.forEach(function (card) {
      Object.keys(savedBundle).forEach(function (name) {
        var input = card.querySelector(
          'input[name="' + name + '"][value="' + savedBundle[name] + '"]:not([disabled])',
        );
        if (input) input.checked = true;
      });
    });
    refresh();
  }
})();
